import React from "react";
import { Helmet } from "react-helmet-async";

export default function VaeAuxPuericulture() {
  // Données structurées : FAQ (allégée car la page a surtout du contenu éditorial)
  const jsonLdFaq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Suis‑je éligible à la VAE AP ?",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "Il n’y a plus de minimum d’expérience strict. Plus vous êtes à l’aise avec la prise en charge des enfants, plus le parcours sera fluide. Des pièces justificatives (attestations, diplômes, stages…) seront toutefois à fournir.",
        },
      },
      {
        "@type": "Question",
        name: "Quelles sont les étapes ?",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "Livret 1 (recevabilité) → Livret 2 (description argumentée + preuves) → préparation à l’oral → jury (validation totale/partielle/non).",
        },
      },
      {
        "@type": "Question",
        name: "Quelle durée prévoir ?",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "Comptez en moyenne 10 à 12 mois avant d’être devant le jury, selon vos acquis et vos disponibilités.",
        },
      },
      {
        "@type": "Question",
        name: "Que comprend l’accompagnement Loustalet ?",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "Vidéos pour le L1 et le L2, cours vidéo et écrits, QCM d’entraînement, et créneaux de rendez‑vous individuels pour l’oral et la relecture de situations.",
        },
      },
    ],
  };

  // Fil d'Ariane
  const jsonLdBreadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Accueil",
        item: "https://www.loustaletformation.fr/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "VAE Auxiliaire de puériculture",
        item: "https://www.loustaletformation.fr/vae-auxiliaire-de-puericulture",
      },
    ],
  };

  // HowTo
  const jsonLdHowTo = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "Réussir sa VAE Auxiliaire de puériculture (DEAP)",
    description:
      "Étapes clés de la démarche VAE : recevabilité, constitution du livret 2 et préparation à l’oral du jury.",
    totalTime: "P10M", // 10 à 12 mois en moyenne
    estimatedCost: [
      {
        "@type": "MonetaryAmount",
        currency: "EUR",
        value: "15-35 / mois",
        name: "Préparation mixte Loustalet Formation",
      },
      {
        "@type": "MonetaryAmount",
        currency: "EUR",
        value: "2000",
        name: "Accompagnement intensif (~30h) dans un centre de formation",
      },
      { "@type": "MonetaryAmount", currency: "EUR", value: "variable", name: "Autonome (livres/ressources)" },
    ],
    step: [
      {
        "@type": "HowToStep",
        name: "Vérifier l’éligibilité",
        text:
          "Plus de minimum d’expérience strict ; s’assurer d’être à l’aise avec la prise en charge des enfants et rassembler ses justificatifs (attestations, diplômes, stages).",
      },
      {
        "@type": "HowToStep",
        name: "Déposer le Livret 1 (recevabilité)",
        text: "Constituer et transmettre le dossier à l’organisme certificateur.",
      },
      {
        "@type": "HowToStep",
        name: "Rédiger le Livret 2",
        text:
          "Décrire vos activités, rassembler les preuves et aligner avec le référentiel DEAP.",
      },
      {
        "@type": "HowToStep",
        name: "Préparer l’oral",
        text:
          "Structurer le discours, valoriser les situations‑clés, s’entraîner (timing, questions sensibles).",
      },
      {
        "@type": "HowToStep",
        name: "Passer devant le jury",
        text:
          "Entretien et décision (validation totale, partielle ou non) ; compléments éventuels à réaliser.",
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>VAE Auxiliaire de puériculture (DEAP) – Accompagnement | L’Oustalet Formation</title>
        <meta
          name="description"
          content="Accompagnement pas à pas pour la VAE AP : L1, L2, cours, vidéos, QCM, RDV individuels et préparation au jury. Tarifs 15–35€/mois."
        />
        <link
          rel="canonical"
          href="https://www.loustaletformation.fr/vae-auxiliaire-de-puericulture"
        />

        {/* Open Graph */}
        <meta
          property="og:title"
          content="VAE Auxiliaire de puériculture (DEAP) – Accompagnement personnalisé"
        />
        <meta
          property="og:description"
          content="Recevez un accompagnement étape par étape : L1, L2, oraux blancs, financements et préparation au jury."
        />
        <meta
          property="og:url"
          content="https://www.loustaletformation.fr/vae-auxiliaire-de-puericulture"
        />
        <meta property="og:type" content="website" />

        {/* Données structurées */}
        <script type="application/ld+json">{JSON.stringify(jsonLdFaq)}</script>
        <script type="application/ld+json">{JSON.stringify(jsonLdBreadcrumb)}</script>
        <script type="application/ld+json">{JSON.stringify(jsonLdHowTo)}</script>
      </Helmet>

      <section className="px-6 py-10 max-w-4xl mx-auto">
        {/* Fil d’Ariane */}
        <nav className="text-sm text-gray-500 mb-6" aria-label="Fil d’Ariane">
          <a href="/" className="underline">Accueil</a>
          <span className="mx-2">›</span>
          <span>VAE Auxiliaire de puériculture</span>
        </nav>

        <h1 className="text-4xl sm:text-5xl font-bold text-rose-600 mb-4">
          VAE Auxiliaire de puériculture : accompagnement personnalisé
        </h1>
        <p className="text-lg text-gray-700 mb-6">
          Recevez un accompagnement étape par étape pour votre VAE DEAP : recevabilité (L1), livret 2, oraux blancs,
          financements et préparation au jury.
        </p>

        {/* Liens d’ancrage rapides */}
        <div className="flex flex-wrap gap-3 mb-8">
          <a href="#eligibilite" className="underline text-rose-700">Éligibilité</a>
          <a href="#etapes" className="underline text-rose-700">Étapes</a>
          <a href="#financement" className="underline text-rose-700">Durée & financements</a>
          <a href="#accompagnement" className="underline text-rose-700">Notre accompagnement</a>
          <a href="#faq" className="underline text-rose-700">FAQ</a>
        </div>

        {/* Éligibilité */}
        <h2 id="eligibilite" className="text-2xl font-semibold mt-10 mb-3">Êtes‑vous éligible ?</h2>
        <ul className="list-disc pl-6 space-y-2 text-gray-700">
          <li>
            Plus besoin d’un minimum d’expérience mais plus vous êtes à l’aise avec la prise en charge des enfants, plus le parcours sera facilité :)
          </li>
          <li>
            Vos missions passées ou actuelles ne recouvrent pas entièrement les activités du référentiel (soins, hygiène, sécurité, développement de l’enfant, relation aux familles…) il vous faudra acquérir les connaissances manquantes (grâce à notre site !).
          </li>
          <li>
            Il vous sera demandé de fournir divers documents : attestations de travail ou de stage, diplômes déjà obtenus, etc.
          </li>
        </ul>

        {/* Étapes */}
        <h2 id="etapes" className="text-2xl font-semibold mt-10 mb-3">Les grandes étapes d’une VAE</h2>
        <ol className="list-decimal pl-6 space-y-2 text-gray-700">
          <li>
            <strong>Livret 1 (recevabilité)</strong> : vérification de l’adéquation entre votre parcours et le diplôme.
          </li>
          <li>
            <strong>Livret 2</strong> : description argumentée de vos activités et mise en relation avec le référentiel.
          </li>
          <li>
            <strong>Préparation à l’oral du jury</strong> : structurer votre discours, valoriser vos situations‑clés et s’entraîner.
          </li>
          <li>
            <strong>Jury</strong> : entretien, décision (validation totale, partielle ou non) et, si besoin, compléments à réaliser.
          </li>
        </ol>

        {/* Accompagnement */}
        <h2 id="accompagnement" className="text-2xl font-semibold mt-10 mb-3">Notre accompagnement en pratique</h2>
        <ul className="list-disc pl-6 space-y-2 text-gray-700">
          <li>
            Des vidéos explicatives pour savoir comment remplir le livret de recevabilité,
          </li>
          <li>
            Des vidéos qui aident à remplir le livret 2 qui sera ensuite envoyée aux jurys,
          </li>
          <li>
            Des cours sous format vidéo et écrit pour apprendre les connaissances attendue de la part d’une auxiliaire de puériculture que vous n’auriez pas encore,
          </li>
          <li>
            Des QCM d'entraînement ludiques,
          </li>
          <li>
            Des créneaux de rendez-vous individuels disponibles pour se préparer à l’oral, demander des avis sur les situations choisies pour remplir le livret 2 etc…
          </li>
        </ul>

        {/* Durée & financements */}
        <h2 id="financement" className="text-2xl font-semibold mt-10 mb-3">Durée, calendrier & financements</h2>
        <p className="text-gray-700 mb-2">
          Comptez en moyenne <strong>10 à 12 mois</strong> avant d’être devant un jury.
        </p>
        <p className="text-gray-700">
          Préparation mixte Loustalet Formation : en ligne, avec cours, vidéos, QCM d’entraînements et possibilité de réserver des RDV
          individuels aux moments clefs de votre parcours VAE si besoin uniquement. <strong>Tarif :</strong> entre <strong>15€</strong> et <strong>35€</strong> par mois selon le format choisi.
        </p>

        {/* CTA */}
        <div className="mt-6 flex flex-wrap gap-3">
          <a href="/tarifs" className="inline-block px-5 py-3 rounded-2xl bg-rose-600 text-white font-semibold shadow">
            Voir les tarifs
          </a>
          <a href="/vae" className="inline-block px-5 py-3 rounded-2xl border border-rose-600 text-rose-700 font-semibold">
            Comprendre la VAE (vue d’ensemble)
          </a>
          <a href="/faq" className="inline-block px-5 py-3 rounded-2xl border border-gray-300 text-gray-700 font-semibold">
            Consulter la FAQ
          </a>
        </div>


        {/* Lien vers la FAQ */}
<h2 className="text-2xl font-semibold mb-3">FAQ</h2>
<p className="text-gray-700 mb-4">
  Retrouvez toutes les réponses à vos questions sur la VAE d’auxiliaire de puériculture,
  les financements, les blocs de compétences et plus encore.
</p>
<a
  href="/faq"
  className="inline-block px-5 py-3 rounded-2xl border border-rose-600 text-rose-700 font-semibold hover:bg-rose-50 transition"
>
  Consulter la FAQ complète
</a>
      </section>
    </>
  );
}
